import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ContentBlocks, IRolesFilter, UserRole } from '../models';
import { CommonModule } from '@angular/common';
import {
  ColouredBlockListComponent,
  CombinedBlockComponent,
  DetailBlockListComponent,
  DetailGridListComponent,
  GroupedInfoBlocksComponent,
  ImageBlockComponent,
  InfoBlockComponent,
  MediaTableComponent,
  QuizListComponent,
} from '@ca/ca-ng-core';
import { Store } from '@ngrx/store';
import { Selectors } from '../store';
import { CaSubscriber } from '@ca/ca-utils';
import { RoleSelectorComponent } from './role-selector.component';
import { combineLatest, filter } from 'rxjs';
import { CustomizeGuidelinesComponent } from "./customize-guidelines/customize-guidelines.component";

@Component({
  selector: 'd4c-plugins',
  imports: [
    CommonModule,
    InfoBlockComponent,
    GroupedInfoBlocksComponent,
    ColouredBlockListComponent,
    DetailGridListComponent,
    DetailBlockListComponent,
    ImageBlockComponent,
    MediaTableComponent,
    QuizListComponent,
    RoleSelectorComponent,
    CombinedBlockComponent,
    CustomizeGuidelinesComponent
  ],
  template: `
    @for (block of blocks; track block) {
      @switch (block.type) {
        @case ('role-selector') {
          <d4c-role-selector />
        }
        @case ('info-block') {
          <ca-info-block [data]="block" />
        }
        @case ('grouped-info-blocks') {
          <ca-grouped-info-blocks [data]="block" />
        }
        @case ('coloured-block-list') {
          <ca-coloured-block-list [data]="block"/>
        }
        @case ('detail-grid') {
          <ca-detail-grid-list [data]="block" />
        }
        @case ('detail-block-list') {
          <ca-detail-block-list [data]="block"></ca-detail-block-list>
        }
        @case ('image-block') {
          <ca-image-block [data]="block.content" />
        }
        @case ('files-table') {
          <ca-media-table [data]="block" />
        }
        @case ('quiz-list') {
          <ca-quiz-list [data]="block" />
        }
        @case ('combined-block') {
          <ca-combined-block [data]="block" />
        }
        @case ('customize-guidelines') {
          <ca-customize-guidelines [data]="block"></ca-customize-guidelines>
        }
      }
    }
  `,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PluginsComponent implements OnInit, OnDestroy {
  page: ContentBlocks | null = null
  blocks: ContentBlocks['content'] = [];
  ready = false;

  private store = inject(Store<{ general: { role: UserRole } }>);
  private page$ = this.store.select(Selectors.selectActivePage);
  private sub = new CaSubscriber();
  private cdr = inject(ChangeDetectorRef);
  private pageWithRoleObserver = ([page, role]: [page: ContentBlocks | null, role: UserRole | null]): void => {
    this.page = page;
    this.blocks =
      this.page && this.page.content
        ? role
          ? filterObjectsByRole(this.page.content, role)
          : this.page.content
        : [];


    this.cdr.markForCheck(); // Ensure view updates
  };

  ngOnInit(): void {
    this.sub.subscribe(
      combineLatest([this.page$, this.store.select(Selectors.selectUserRole)]).pipe(filter(([page, role]) => !!page)),
      this.pageWithRoleObserver
    );
  }

  ngOnDestroy(): void {
    this.sub.closeSubscriptions();
  }
}

function filterObjectsByRole(items: ContentBlocks['content'], role: keyof IRolesFilter | null): ContentBlocks['content'] {
  if (role === null) { return items; }
  if (!Array.isArray(items)) return items;

  return items
    .map(item => filterBlockByRole(item, role))
    .filter((item): item is ContentBlocks['content'][number] => item !== null);
}

function filterBlockByRole(
  item: ContentBlocks['content'][number],
  role: keyof IRolesFilter
): ContentBlocks['content'][number] | null {
  if (!shouldKeepBlock(item, role)) {
    return null;
  }

  switch (item.type) {
    case 'grouped-info-blocks': {
      const content = item.content.filter(child => matchesRole(child, role));
      return content.length ? ({ ...item, content } as typeof item) : null;
    }
    case 'combined-block': {
      const content = item.content.filter(child => matchesRole(child, role));
      return content.length ? ({ ...item, content } as typeof item) : null;
    }
    case 'detail-block-list': {
      const content = item.content.filter(child => matchesRole(child, role));
      return content.length ? ({ ...item, content } as typeof item) : null;
    }
    case 'coloured-block-list': {
      const content = item.content.filter(child => matchesRole(child, role));
      return content.length ? ({ ...item, content } as typeof item) : null;
    }
    case 'detail-grid': {
      const content = item.content.filter(child => matchesRole(child, role));
      return content.length ? ({ ...item, content } as typeof item) : null;
    }
    default:
      return item;
  }
}

function shouldKeepBlock(item: ContentBlocks['content'][number], role: keyof IRolesFilter): boolean {
  if (item.type === 'customize-guidelines' || item.type === 'files-table' || item.type === 'image-block') {
    return true;
  }

  if (item.type === 'role-selector') {
    return false;
  }

  return matchesRole(item, role);
}

function matchesRole(target: unknown, role: keyof IRolesFilter): boolean {
  if (!isRoleFiltered(target)) {
    return true;
  }

  const { filters } = target;
  if (!filters) {
    return false;
  }

  return filters[role] ?? false;
}

function isRoleFiltered(value: unknown): value is { filters: IRolesFilter | null | undefined } {
  return typeof value === 'object' && value !== null && 'filters' in value;
}
